import { CodeHighlight } from "@mantine/code-highlight";
import { ActionIcon, Anchor, Button, Code, Divider, Fieldset, Group, Stack, Table, TextInput } from "@mantine/core";
import { IconEdit, IconOutbound, IconRouteAltRight, IconSearch, IconTrash } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodResolver } from "mantine-form-zod-resolver";
import { httpPostGraphql, StrictUndef } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { useIngestRuleActions } from "shared/components/ingest/actions";
import {
    createIngestRuleFormState,
    IngestRuleFormProvider,
    ingestRuleFormStateSchema,
    useIngestRuleForm,
} from "shared/components/ingest/context";
import { IngestRuleForm } from "shared/components/ingest/IngestRuleForm";
import { ComposeFormProvider, createComposeFormState, useComposeForm } from "shared/components/message/compose/context";
import { ComposeForm } from "shared/components/message/ComposeForm";
import { useSearch } from "shared/hooks/useSearch";
import { useAside } from "shared/utils/aside";
import { maybe } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { ComposeDetailsFragment, IngestRuleDetailsFragment } from "src/gql/graphql";

const Page = () => {
    const { data } = useSuspenseQuery(query.ingestRules);
    const aside = useAside();
    const { bindSearchProps, bySearchingOn } = useSearch();
    const actions = useIngestRuleActions();

    function handleDefineRule() {
        const Panel = () => {
            const {
                create,
                mutations: {
                    createIngestRule: { isIdle },
                },
            } = useIngestRuleActions();

            const form = useIngestRuleForm({
                initialValues: { matchExpr: "", matchKey: "" },
                validate: zodResolver(ingestRuleFormStateSchema),
            });

            return (
                <Stack>
                    <IngestRuleFormProvider form={form}>
                        <IngestRuleForm />
                        <Divider mx="-md" />
                    </IngestRuleFormProvider>
                    <Group justify="flex-end">
                        <Button
                            variant="default"
                            loading={!isIdle}
                            onClick={() => {
                                if (form.validate().hasErrors) return;
                                create(form.getValues())
                                    .then(() => aside.dismiss())
                                    .catch(notify.catch);
                            }}
                        >
                            Save
                        </Button>
                    </Group>
                </Stack>
            );
        };

        aside.present({
            label: "Define Ingest Rule",
            component: <Panel />,
            mode: "drawer",
        });
    }

    function handleUpdateIngestRule(rule: StrictUndef<IngestRuleDetailsFragment>) {
        const Panel = () => {
            const form = useIngestRuleForm({
                initialValues: createIngestRuleFormState(rule),
                validate: zodResolver(ingestRuleFormStateSchema),
            });

            const {
                update,
                mutations: {
                    updateIngestRule: { isIdle },
                },
            } = useIngestRuleActions();

            return (
                <Stack>
                    <IngestRuleFormProvider form={form}>
                        <IngestRuleForm />
                    </IngestRuleFormProvider>

                    <Group justify="flex-end">
                        <Button
                            variant="default"
                            loading={!isIdle}
                            onClick={() => {
                                if (form.validate().hasErrors) return;
                                update(rule.id, form.getValues())
                                    .then(() => aside.dismiss())
                                    .catch(notify.catch);
                            }}
                        >
                            Update
                        </Button>
                    </Group>
                </Stack>
            );
        };

        aside.present({
            label: "Update Ingest Rule",
            component: <Panel />,
            mode: "drawer",
        });
    }

    function handleTemplatePreview(composeDetails: StrictUndef<ComposeDetailsFragment>) {
        const Preview = (props: { composeDetails: StrictUndef<ComposeDetailsFragment> }) => {
            const form = useComposeForm({
                initialValues: { ...createComposeFormState(props.composeDetails), meta: { mode: "read" } },
            });

            return (
                <ComposeFormProvider form={form}>
                    <Fieldset disabled variant="unstyled">
                        <ComposeForm />
                    </Fieldset>
                </ComposeFormProvider>
            );
        };

        aside.present({ label: "Template Preview", component: <Preview composeDetails={composeDetails} />, mode: "drawer" });
    }

    return (
        <Content paper>
            <Content.Heading backable>Ingest Rules</Content.Heading>

            <Content.Action>
                <Group>
                    <TextInput leftSection={<IconSearch size={16} />} placeholder="Search by match key..." {...bindSearchProps()} />
                    <Button variant="default" leftSection={<IconRouteAltRight size={16} />} onClick={handleDefineRule}>
                        Define Rule
                    </Button>
                </Group>
            </Content.Action>

            <Table striped withRowBorders>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Match On</Table.Th>
                        <Table.Th>With Pattern</Table.Th>
                        <Table.Th>Destination Template</Table.Th>
                        <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {data.length == 0 && (
                        <Table.Tr>
                            <Table.Td ta="center" colSpan={4}>
                                No Ingest Rules
                            </Table.Td>
                        </Table.Tr>
                    )}
                    {data.filter(bySearchingOn((it) => it.matchKey)).map((rule) => (
                        <Table.Tr key={rule.id}>
                            <Table.Td>
                                <Code>{rule.matchKey}</Code>
                            </Table.Td>
                            <Table.Td>
                                <CodeHighlight code={`/${rule.matchExpr}/`} language="regex" withCopyButton={false} background="none" />
                            </Table.Td>
                            <Table.Td>
                                <Anchor>
                                    <Button
                                        variant="transparent"
                                        leftSection={<IconOutbound size={16} />}
                                        td="underline"
                                        onClick={() => maybe(rule.destinationTemplate?.message)?.take(handleTemplatePreview)}
                                    >
                                        {rule.destinationTemplate?.message.subject}
                                    </Button>
                                </Anchor>
                            </Table.Td>
                            <Table.Th>
                                <ActionIcon.Group>
                                    <ActionIcon variant="default" onClick={() => handleUpdateIngestRule(rule)} aria-label="Edit rule">
                                        <IconEdit size={16} />
                                    </ActionIcon>
                                    <ActionIcon
                                        variant="default"
                                        onClick={() => void actions.delete(rule.id).catch(notify.catch)}
                                        aria-label="Delete rule"
                                    >
                                        <IconTrash size={16} />
                                    </ActionIcon>
                                </ActionIcon.Group>
                            </Table.Th>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        </Content>
    );
};

const query = {
    ingestRules: queryOptions({
        queryKey: qk("support", "ingest-rules"),
        select: ({ ingest: { rules } }) => rules,
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query SupportIngestManage {
                        ingest {
                            rules {
                                ...IngestRuleDetails
                                destinationTemplate {
                                    id
                                    message {
                                        ...ComposeDetails
                                    }
                                }
                            }
                        }
                    }
                `),
            ),
    }),
};

/** @public */
export const Route = createFileRoute("/_auth/_dev/devtools/ingest")({
    component: Page,
});
